// frontend/src/pages/Home.js
import React, { useEffect, useMemo, useState } from "react";
import ActivityUpload from "./ActivityUpload";
import { api } from "api";
import { Capacitor } from "@capacitor/core";

import AppHeader from "../components/AppHeader";
import ProfileCard from "../components/ProfileCard";
import ActivityCard from "../components/ActivityCard";
import ActivityFormModal from "../components/ActivityFormModal";
import CirclesPanel from "../components/CirclesPanel";
import FeedScopeSelect from "../components/FeedScopeSelect";
import ConnectionsCard from "../components/ConnectionsCard";

import MonthlyCalendarModal from "../components/MonthlyCalendarModal";
import MonthlyButtonWithStreak from "../components/MonthlyButtonWithStreak";
import WeeklyCalendarModal from "../components/WeeklyCalendarModal";
import WeeklyPlanModal from "../components/WeeklyPlanModal";
import GoalsNote from "../components/GoalsNote";
import GoalsByMonth from "../components/GoalsByMonth";
import RemindersModal from "../components/RemindersModal";
import { isIOS } from "../utils/platform";
import FoldCard from "../components/FoldCard";
import PlannerQuickPanel from "../components/PlannerQuickPanel";

import { toAbsolute } from "../utils/url";
import { DEFAULT_CIRCLE_IMAGE } from "../utils/blank-image";

import { Preferences } from "@capacitor/preferences";
import { X } from "phosphor-react";
import { computeCalendarMarks } from "../utils/streaks";

/* ---------- iOS-safe auth ---------- */
const getToken = async () => {
  try {
    const pref = await Preferences.get({ key: "auth_token" });
    if (pref?.value) return pref.value;
  } catch {}
  if (typeof window !== "undefined") {
    return (
      localStorage.getItem("token") ||
      sessionStorage.getItem("token") ||
      null
    );
  }
  return null;
};

const getAuthHeaders = async () => {
  const token = await getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/* ---------- Responsive / native check ---------- */
export function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const nativeOrIOS =
    /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    (typeof Capacitor?.isNativePlatform === "function" &&
      Capacitor.isNativePlatform());

  return isMobile || nativeOrIOS;
}

/* ---------- UI helpers ---------- */
const ThickMonthWord = ({ text = "", className = "" }) => {
  const upper = (text || "").toUpperCase();
  return (
    <div className={`w-full flex justify-center ${className}`}>
      <span
        className={`
          inline-block text-center select-none
          text-blue-600 font-black uppercase leading-none
          tracking-[-0.04em]
        `}
        style={{
          width: "50%",
          fontSize: "clamp(32px, 6vw, 56px)",
          lineHeight: 1,
          paddingLeft: "4px",
          paddingRight: "4px",
        }}
        aria-label={upper}
      >
        {upper}
      </span>
    </div>
  );
};

// date -> "YYYY-MM-DD"
const ymd = (d) => {
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// group activities by day
const groupByDay = (list = []) => {
  const map = {};
  list.forEach((a) => {
    const key = ymd(a.timestamp || a.created_at);
    (map[key] ||= []).push(a);
  });
  Object.keys(map).forEach((k) => {
    map[k].sort(
      (a, b) =>
        new Date(a.timestamp || a.created_at) -
        new Date(b.timestamp || b.created_at)
    );
  });
  return map;
};

// latest -> oldest
const sortByTimestampDesc = (arr = []) =>
  [...arr].sort(
    (a, b) =>
      new Date(b.timestamp || b.created_at) -
      new Date(a.timestamp || a.created_at)
  );

const authorIdOf = (a) =>
  a?.user?.id ?? a?.user_id ?? a?.author_id ?? null;

const filterByFollowing = (activities = [], followingIdSet, meId) => {
  return activities.filter((a) => {
    const aid = authorIdOf(a);
    if (!aid) return false;
    if (meId && aid === meId) return true;
    return followingIdSet && followingIdSet.size > 0 && followingIdSet.has(aid);
  });
};

const Home = () => {
  const [user, setUser] = useState(null);
  const isMobile = useIsMobile();
  const onIOS = !!isIOS;

  const leftRailRef = React.useRef(null);
  const rightRailRef = React.useRef(null);

  const [desktopRailHeight, setDesktopRailHeight] =
    useState(null);

  const [feedActivities, setFeedActivities] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  const [joinedCircles, setJoinedCircles] = useState([]);

  const [myActivities, setMyActivities] = useState([]);

  const [editingActivity, setEditingActivity] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  const [showMonthly, setShowMonthly] = useState(false);
  const [showWeekly, setShowWeekly] = useState(false);
  const [showWeekPlan, setShowWeekPlan] = useState(false);
  const [showReminders, setShowReminders] = useState(false);
  const [showGoalsByMonth, setGoalsByMonth] = useState(false);
  const [showPlannerQuick, setShowPlannerQuick] = useState(false);
  const [externalModalOpen, setExternalModalOpen] = useState(false);

  const [followingIds, setFollowingIds] = useState(new Set());

  const [bootLoading, setBootLoading] = useState(true);
  const [bootError, setBootError] = useState("");

  // "following" | "mine" | `circle:${id}`
  const getInitialScope = () => {
    const q = new URLSearchParams(window.location.search).get("scope");
    return q === "mine" || (q && q.startsWith("circle:")) ? q : "following";
  };
  const [feedScope, setFeedScope] = useState(getInitialScope());

  useEffect(() => {
    if (!user) return;

    const updateDesktopRailHeight = () => {
      if (
        onIOS ||
        isMobile ||
        window.innerWidth < 768
      ) {
        setDesktopRailHeight(null);
        return;
      }

      const leftRect =
        leftRailRef.current?.getBoundingClientRect();

      const rightRect =
        rightRailRef.current?.getBoundingClientRect();

      const leftHeight =
        leftRect?.height || 0;

      const rightHeight =
        rightRect?.height || 0;

      /*
      * Both rails start on the same grid row.
      * This gives us the usable viewport height from
      * the top of the columns to the bottom of the screen.
      */
      const railTop =
        leftRect?.top ??
        rightRect?.top ??
        0;

      const availableViewportHeight = Math.max(
        0,
        window.innerHeight - railTop
      );

      /*
      * Middle-column desktop height:
      *
      * - If either side rail is taller than the visible screen,
      *   match the taller side rail.
      *
      * - If BOTH side rails are shorter than the visible screen,
      *   fill the remaining visible screen height.
      *
      * The feed inside this fixed-height middle column can then
      * scroll independently.
      */
      const targetHeight = Math.ceil(
        Math.max(
          availableViewportHeight,
          leftHeight,
          rightHeight
        )
      );

      if (targetHeight > 0) {
        setDesktopRailHeight(targetHeight);
      }
    };

    const frame = requestAnimationFrame(
      updateDesktopRailHeight
    );

    const observer = new ResizeObserver(
      updateDesktopRailHeight
    );

    if (leftRailRef.current) {
      observer.observe(
        leftRailRef.current
      );
    }

    if (rightRailRef.current) {
      observer.observe(
        rightRailRef.current
      );
    }

    window.addEventListener(
      "resize",
      updateDesktopRailHeight
    );

    return () => {
      cancelAnimationFrame(frame);

      observer.disconnect();

      window.removeEventListener(
        "resize",
        updateDesktopRailHeight
      );
    };
  }, [
    user,
    isMobile,
    onIOS,
  ]);

  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("scope", feedScope);
    window.history.replaceState({}, "", url);
  }, [feedScope]);

  useEffect(() => {
    const handler = (e) => {
      const s = e.detail?.scope;
      if (typeof s === "string") setFeedScope(s);
    };
    window.addEventListener("feed:set-scope", handler);
    return () => window.removeEventListener("feed:set-scope", handler);
  }, []);

  // Listen for ConnectionsModal open/close coming from ProfileCard
  useEffect(() => {
    const onOpen = () => setExternalModalOpen(true);
    const onClose = () => setExternalModalOpen(false);
    window.addEventListener("connections:open", onOpen);
    window.addEventListener("connections:close", onClose);
    return () => {
      window.removeEventListener("connections:open", onOpen);
      window.removeEventListener("connections:close", onClose);
    };
  }, []);


  const parseScope = (v) => {
    if (v === "following") return { type: "following" };
    if (v === "mine") return { type: "mine" };
    if (String(v).startsWith("circle:")) {
      const id = Number(String(v).split(":")[1]);
      return { type: "circle", circleId: Number.isFinite(id) ? id : undefined };
    }
    return { type: "following" };
  };

  const monthLabel = useMemo(
    () =>
      new Date()
        .toLocaleString("en-US", { month: "short" })
        .slice(0, 3)
        .toUpperCase(),
    []
  );

  const fetchFollowingIds = async (meId, headers) => {
    try {
      const { data } = await api.get(`/connections/following/${meId}`, {
        headers,
      });
      const list = Array.isArray(data) ? data : [];
      const ids = new Set(list.map((u) => u?.id).filter(Boolean));
      setFollowingIds(ids);
      return ids;
    } catch (e) {
      console.error("Failed to load following:", e);
      setFollowingIds(new Set());
      return new Set();
    }
  };

  const runBootstrap = async () => {
    setBootLoading(true);
    setBootError("");

    const token = await getToken();
    if (!token) {
      setBootLoading(false);
      setBootError("You're not logged in. Please sign in to view your feed.");
      return;
    }

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);

    try {
      const headers = await getAuthHeaders();

      const userRes = await api.get("/users/me", {
        headers,
        signal: controller.signal,
      });
      setUser(userRes.data);

      const circlesRes = await api.get(`/users/${userRes.data.id}/groups`, {
        headers,
        signal: controller.signal,
      });

      const circlesData = Array.isArray(circlesRes.data)
        ? circlesRes.data
        : circlesRes.data?.items || [];

      setJoinedCircles(
        circlesData.map((circle) => ({
          ...circle,
          image_url:
            toAbsolute(
              circle?.image_url ||
              circle?.icon_url ||
              circle?.avatar_url
            ) || DEFAULT_CIRCLE_IMAGE,
        }))
      );

      const mineRes = await api.get(`/users/${userRes.data.id}/activities`, {
        headers,
        signal: controller.signal,
      });
      setMyActivities(Array.isArray(mineRes.data) ? mineRes.data : []);

      const ids = await fetchFollowingIds(userRes.data.id, headers);

      const initialScope = getInitialScope();

      await fetchFeedForScope(
        initialScope,
        null,
        userRes.data,
        ids
      );
    } catch (err) {
      console.error(err);
      const msg =
        err?.name === "AbortError"
          ? "The request timed out. Please try again."
          : err?.response?.data?.detail ||
            err?.message ||
            "Could not reach the API.";
      const friendly =
        /Failed to fetch|CORS|No 'Access-Control-Allow-Origin'|NetworkError/i.test(
          String(msg)
        )
          ? "We couldn’t reach the API from this origin (CORS). Ask the server to allow https://thirdplace.ing or use a same-origin proxy."
          : msg;
      setBootError(friendly);
    } finally {
      clearTimeout(timeout);
      setBootLoading(false);
    }
  };

  useEffect(() => {
    runBootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handler = (e) => setEditingActivity(e.detail);
    window.addEventListener("open-edit-activity", handler);
    return () => window.removeEventListener("open-edit-activity", handler);
  }, []);

  const fetchFeedForScope = async (
    scopeValue,
    tag = null,
    me = user,
    ids = followingIds
  ) => {
    const scope =
      typeof scopeValue === "string" ? parseScope(scopeValue) : scopeValue;
    const headers = await getAuthHeaders();

    const getAndSet = async (url, attachMe = false, onlyFollowing = false) => {
      const finalUrl =
        tag && !/[\?&]tag=/.test(url)
          ? `${url}${url.includes("?") ? "&" : "?"}tag=${encodeURIComponent(
              tag
            )}`
          : url;

      const res = await api.get(finalUrl, { headers });
      let data = Array.isArray(res.data) ? res.data : [];

      if (attachMe && me) {
        data = data.map((a) => (a && !a.user ? { ...a, user: me } : a));
      }

      if (onlyFollowing) {
        data = filterByFollowing(data, ids, me?.id);
      }

      setFeedActivities(sortByTimestampDesc(data));
    };

    try {
      if (scope?.type === "mine" && me?.id) {
        await getAndSet(`/users/${me.id}/activities`, true, false);
        return;
      }

      if (scope?.type === "circle" && scope.circleId) {
        try {
          await getAndSet(`/groups/${scope.circleId}/feed`, false, false);
          return;
        } catch {
          await getAndSet(`/api/feed?circle_id=${scope.circleId}`, false, false);
          return;
        }
      }

      // default: Following — fetch a broad feed then client-filter
      await getAndSet("/api/feed?scope=following", false, true);
    } catch (err) {
      console.error("Failed to fetch feed:", err);
      setFeedActivities([]);
    }
  };

  const container = "max-w-7xl mx-auto px-6 md:px-10 xl:px-16";

  const handleTagClick = (tag) => {
    setSelectedTag(tag);
    fetchFeedForScope(feedScope, tag);
  };

  const clearTag = () => {
    setSelectedTag(null);
    fetchFeedForScope(feedScope, null);
  };

  const handleUploadOrEditSuccess = async () => {
    setEditingActivity(null);

    try {
      const headers = await getAuthHeaders();

      let freshMyActivities = [];

      if (user?.id) {
        const mineRes = await api.get(
          `/users/${user.id}/activities`,
          { headers }
        );

        freshMyActivities = Array.isArray(mineRes.data)
          ? mineRes.data
          : [];

        setMyActivities(freshMyActivities);
      }

      /*
      * When currently viewing "My activities", use the fresh response
      * directly instead of immediately making another competing request.
      */
      if (feedScope === "mine") {
        const activitiesWithUser = freshMyActivities.map((activity) =>
          activity?.user
            ? activity
            : {
                ...activity,
                user,
              }
        );

        setFeedActivities(
          sortByTimestampDesc(activitiesWithUser)
        );

        return;
      }

      await fetchFeedForScope(
        feedScope,
        selectedTag
      );
    } catch (error) {
      console.error(
        "Failed to refresh activities after update:",
        error
      );

      await fetchFeedForScope(
        feedScope,
        selectedTag
      );
    }
  };

  const handleActivityUpdated = (updatedActivity) => {
    if (!updatedActivity?.id) return;

    setFeedActivities((current) =>
      current.map((activity) =>
        activity.id === updatedActivity.id
          ? {
              ...activity,
              ...updatedActivity,
            }
          : activity
      )
    );

    setMyActivities((current) =>
      current.map((activity) =>
        activity.id === updatedActivity.id
          ? {
              ...activity,
              ...updatedActivity,
              user: updatedActivity.user || activity.user || user,
            }
          : activity
      )
    );
  };

  useEffect(() => {
    if (user) fetchFeedForScope(feedScope, selectedTag);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedScope, user]);

  const myActivitiesByDay = useMemo(
    () => groupByDay(myActivities),
    [myActivities]
  );

  const streakDays = useMemo(
    () =>
      computeCalendarMarks({
        activityDates: Object.keys(myActivitiesByDay || {}),
      }).streakCount,
    [myActivitiesByDay]
  );

  // Derived flag: any modal/popup open?
  const anyModalOpen =
    showMonthly ||
    showWeekly ||
    showWeekPlan ||
    showReminders ||
    showGoalsByMonth ||
    !!editingActivity ||
    uploadOpen ||
    showPlannerQuick ||
    externalModalOpen;

  if (bootLoading) {
    return (
      <div className="min-h-screen bg-blue-50 text-[#181411] flex items-center justify-center px-6">
        <p className="text-sm font-medium text-[#6f665d]">Loading...</p>
      </div>
    );
  }

  if (bootError) {
    return (
      <div className="min-h-screen bg-blue-50 text-[#181411] flex items-center justify-center p-6">
        <div className="max-w-lg w-full rounded-[32px] border border-slate-100 bg-slate-50 px-6 py-6 space-y-4">
          <h2 className="text-lg font-semibold text-blue-700">
            Can't load your feed
          </h2>
          <p className="text-sm text-gray-700">{bootError}</p>
          <div className="flex items-center gap-3">
            <a href="/login"
              className="px-3 py-1 text-sm !min-h-0 !min-w-0 rounded-full bg-blue-600 hover:ring-2 hover:ring-blue-400 text-white"
            >
              Retry
            </a>
            <a href="/login" className="text-sm text-blue-600 hover:text-blue-400 font-semibold">
              Sign in
            </a>
          </div>
          <details className="text-xs text-gray-500">
            <summary>Technical details</summary>
            <p>
              If you host the API on a different domain, ensure it returns
              <code> Access-Control-Allow-Origin: https://thirdplace.ing </code>
              (or <code>*</code>) and includes the appropriate methods and
              headers
            </p>
          </details>
        </div>
      </div>
    );
  }

  if (!user) {
    return <div className="p-8">Please sign in.</div>;
  }

  return (
    <div className="app-shell bg-blue-50 text-[#181411]">
      <header className="app-header">
        <AppHeader user={user} />
      </header>

      <main className={`app-content w-full ${container} pt-[calc(64px+var(--safe-top))] md:mt-4 md:pb-14 px-0 py-8`}>
        <div
          className="
            grid grid-cols-1
            md:grid-cols-[1fr_minmax(0,2fr)_1fr]
            gap-10
            md:overflow-visible
          "
          style={{ height: "auto" }}
        >
          {/* LEFT (always shown; sticky on desktop) — left as-is */}
          <div className="-mb-8 md:self-start">
            <div
              ref={leftRailRef}
              className="md:sticky top-0 md:h-max"
              style={{ height: "max-content" }}
            >
              <ProfileCard
                user={user}
                viewer={user}
                isSelf
              />

              {!(onIOS || isMobile) && (
                <div className="hidden md:block mt-4">
                  <PlannerQuickPanel
                    keySuffix="home"
                    monthLabel={new Date()
                      .toLocaleString("en-US", { month: "short" })
                      .toUpperCase()}
                    onMonthly={() => setShowMonthly(true)}
                    onWeekly={() => setShowWeekly(true)}
                    onWeekPlan={() => setShowWeekPlan(true)}
                    onGoals={() => setGoalsByMonth(true)}
                    onReminders={() => setShowReminders(true)}
                    streakDays={streakDays}
                    activitiesByDay={myActivitiesByDay}
                  />
                </div>
              )}
            </div>
          </div>

          {/* MIDDLE (only the FEED scrolls on desktop) */}
          <section
            className={`md:col-span-1 flex flex-col min-h-0 ${
              selectedTag ? "space-y-6" : "space-y-4"
            } ${
              (onIOS || isMobile)
                ? "overflow-visible"
                : "overflow-hidden"
            }`}
            style={
              (onIOS || isMobile)
                ? undefined
                : {
                    height:
                      desktopRailHeight != null
                        ? `${desktopRailHeight}px`
                        : undefined,
                  }
            }
          >
            <div
              className={`relative ml-2.5 mt-2 self-stretch flex items-center justify-between sm:mt-0 ${
                anyModalOpen ? "z-0" : "z-[40]"
              }`}
            >
              <FeedScopeSelect
                value={feedScope}
                onChange={setFeedScope}
                circles={joinedCircles}
              />
            </div>

            <section className="flex min-h-0 flex-1 flex-col">

            {selectedTag && (
              <div className="ml-6 mb-3 -mt-4 flex items-center gap-2">
                <span className="text-sm text-gray-600">Filtering by tag:</span>

                <span className="!min-h-0 !min-w-0 rounded-full bg-blue-100 px-3 py-1 text-sm text-blue-700">
                  #{selectedTag}
                </span>

                <button
                  onClick={clearTag}
                  className="!min-h-0 !min-w-0 py-1 text-sm text-gray-500 hover:font-semibold"
                >
                  clear
                </button>
              </div>
            )}


            {/* Scrollable FEED area (desktop-only) */}
            <div
              className={`relative z-0 min-h-0 flex-1 space-y-4 ${
                (onIOS || isMobile)
                  ? "overflow-visible"
                  : "overflow-y-auto overscroll-y-contain no-scrollbar pb-28"
              }`}
            >
              {feedActivities.length === 0 ? (
                <div className="flex min-h-[280px] items-start mt-[60px] sm:items-center sm:mt-0 justify-center px-6 text-center">
                  <div>
                    <p className="text-sm font-semibold text-gray-700">
                      Upload an activity to get started!
                    </p>

                    <p className="text-sm text-gray-500">
                      Connect with people to see what they'e up to
                    </p>
                  </div>
                </div>
              ) : (
                feedActivities.map((activity) => (
                  <ActivityCard
                    key={activity.id}
                    activity={activity}
                    currentUser={user}
                    onTagClick={handleTagClick}
                    onUpdated={handleActivityUpdated}
                  />
                ))
              )}
            </div>
            </section>
          </section>

          {/* RIGHT (sticky on desktop) — Upload button goes above Circles panel */}
          {!(onIOS || isMobile) && (
            <aside className="hidden md:block self-stretch">
              <div
                ref={rightRailRef}
                className={`sticky md:z-0`}
                style={{
                  top: "calc(env(safe-area-inset-top) + 0px)",
                  height: "max-content",
                }}
              >
                {/* Desktop Upload button */}
                <div className="mb-4 pointer-events-auto">
                  <button
                    type="button"
                    onClick={() => setUploadOpen(true)}
                    className="w-full rounded-full bg-blue-600 text-[16px] px-4 py-2 font-semibold text-white transition hover:bg-blue-700"
                  >
                    Upload Activity
                  </button>
                </div>

                <CirclesPanel
                  joinedCircles={joinedCircles}
                  self
                  ownerName={
                    (
                      user?.display_name ||
                      user?.name ||
                      [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
                      user?.username ||
                      (user?.id ? `User #${user.id}` : "You")
                    ).trim()
                  }
                />
                <div className="mt-4">
                  <ConnectionsCard
                    meId={user?.id}
                    viewerId={user?.id}
                    ownerName={
                      (
                        user?.display_name ||
                        user?.name ||
                        [user?.first_name, user?.last_name]
                          .filter(Boolean)
                          .join(" ") ||
                        user?.username ||
                        "You"
                      ).trim()
                    }
                  />
                </div>
              </div>
            </aside>
          )}
        </div>

        <div className="app-footer-pad" />
      </main>

      {/* Desktop Upload Modal */}
      {!(onIOS || isMobile) && uploadOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setUploadOpen(false)}
          />

          <div
            className="
              relative z-10
              flex w-full max-w-xl flex-col
              max-h-[90dvh]
              overflow-hidden rounded-2xl
              bg-slate-50 shadow-xl
            "
          >
            <div className="flex shrink-0 items-center justify-between px-5 pt-4 pb-2">
              <h3 className="font-black tracking-[-0.01em] text-blue-600 text-[18px]">
                Upload Activity
              </h3>

              <button
                type="button"
                onClick={() => setUploadOpen(false)}
                className="grid !min-h-0 !min-w-0 h-8 w-8 place-items-center text-gray-500"
                aria-label="Close"
              >
                <X size={18} weight="bold" />
              </button>
            </div>

            <div className="min-h-0 max-h-[calc(90dvh-56px)] overflow-y-auto">
              <ActivityUpload
                fetchFeed={async () => {
                  await handleUploadOrEditSuccess();
                }}
                onClose={() => setUploadOpen(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <MonthlyCalendarModal
        open={showMonthly}
        onClose={() => setShowMonthly(false)}
        activitiesByDay={myActivitiesByDay}
        currentUser={user}
        streakDays={streakDays}
      />
      <WeeklyCalendarModal
        open={showWeekly}
        onClose={() => setShowWeekly(false)}
        activitiesByDay={myActivitiesByDay}
        currentUser={user}
      />
      <WeeklyPlanModal
        open={showWeekPlan}
        onClose={() => setShowWeekPlan(false)}
        keySuffix="home"
      />
      <GoalsByMonth
        open={showGoalsByMonth}
        onClose={() => setGoalsByMonth(false)}
        keySuffix="home"
      />
      <RemindersModal
        open={showReminders}
        onClose={() => setShowReminders(false)}
        storageKey="home:reminders"
      />

      {editingActivity && (
        <ActivityFormModal
          activity={editingActivity}
          onClose={() => setEditingActivity(null)}
          onSuccess={handleUploadOrEditSuccess}
        />
      )}
    </div>
  );
};

export default Home;
